import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerProfileExtrasApi } from '../../api/partner-profile-extras';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/Toast';

interface FormValues {
  yearsOfExperience: number; majorProgrammes: string; implementationExperience: string; technicalStaffingNotes: string;
  mealNotes: string; reportingNotes: string; dataManagementNotes: string; communityEngagementNotes: string;
}

export function PartnerProgrammeCapacityTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const dataQuery = useQuery({ queryKey: ['partner-programme-capacity', partnerId], queryFn: () => partnerProfileExtrasApi.getProgrammeCapacity(partnerId) });
  const { register, handleSubmit, reset } = useForm<FormValues>();

  useEffect(() => {
    if (dataQuery.data?.data) {
      const d = dataQuery.data.data;
      reset({
        yearsOfExperience: d.years_of_experience ?? undefined, majorProgrammes: d.major_programmes ?? '', implementationExperience: d.implementation_experience ?? '',
        technicalStaffingNotes: d.technical_staffing_notes ?? '', mealNotes: d.meal_notes ?? '', reportingNotes: d.reporting_notes ?? '',
        dataManagementNotes: d.data_management_notes ?? '', communityEngagementNotes: d.community_engagement_notes ?? '',
      });
    }
  }, [dataQuery.data, reset]);

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => partnerProfileExtrasApi.upsertProgrammeCapacity(partnerId, { ...values, yearsOfExperience: Number(values.yearsOfExperience) || undefined }),
    onSuccess: () => { showSuccess('Programme capacity saved.'); queryClient.invalidateQueries({ queryKey: ['partner-programme-capacity', partnerId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save.'),
  });

  return (
    <div className="panel">
      <div className="panel-body">
        <form onSubmit={handleSubmit((values) => saveMutation.mutate(values))} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label>Years of Experience<input type="number" {...register('yearsOfExperience')} /></label>
          <label>Major Programmes<textarea {...register('majorProgrammes')} /></label>
          <label>Implementation Experience<textarea {...register('implementationExperience')} /></label>
          <label>Technical Staffing<textarea {...register('technicalStaffingNotes')} /></label>
          <label>M&E<textarea {...register('mealNotes')} /></label>
          <label>Reporting<textarea {...register('reportingNotes')} /></label>
          <label>Data Management<textarea {...register('dataManagementNotes')} /></label>
          <label>Community Engagement<textarea {...register('communityEngagementNotes')} /></label>
          <button type="submit" disabled={saveMutation.isPending} style={{ alignSelf: 'flex-start' }}>{saveMutation.isPending ? 'Saving…' : 'Save'}</button>
        </form>
      </div>
    </div>
  );
}
